import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/** Every step, in order. A product uses only the option steps it needs. */
const ORDER = ['item', 'option0', 'option1', 'option2', 'art', 'qty'];
const VECTOR_TYPES = ['svg', 'ai', 'eps', 'pdf'];
const RASTER_TYPES = ['png', 'jpg', 'jpeg'];
/** Colors whose names are not CSS color names. A swatch set in Shopify admin wins over these. */
const SWATCHES = { royal: '#2450a6', walnut: '#6b4423' };

/**
 * One option of the chosen product, as Shopify rendered it.
 * @typedef {object} ProductOption
 * @property {string} name - For example "Color".
 * @property {HTMLElement[]} tiles - One per value.
 * @property {string} id - The option value id sent to Shopify: the customer's choice, or Shopify's default.
 */

/**
 * @typedef {object} GiftsBuilderRefs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} [fallback]
 * @property {HTMLElement} chips
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} price
 * @property {HTMLElement} [priceNote]
 * @property {HTMLElement} stage
 * @property {HTMLElement} progress
 * @property {HTMLElement} status
 * @property {HTMLElement} uploadBox
 * @property {HTMLInputElement} file
 * @property {HTMLElement} fileVector
 * @property {HTMLElement} fileRaster
 * @property {HTMLElement} fileType
 * @property {HTMLButtonElement} [vectorize]
 * @property {HTMLElement} [ownershipNotice]
 * @property {HTMLElement} textBox
 * @property {HTMLTextAreaElement} text
 * @property {HTMLElement} [laterBox]
 * @property {HTMLLabelElement} quantityLabel
 * @property {HTMLInputElement} quantity
 * @property {HTMLElement} packLine
 * @property {HTMLElement} total
 * @property {HTMLTextAreaElement} notes
 * @property {HTMLTemplateElement} [moneyFormat]
 * @property {HTMLButtonElement} submit
 * @property {HTMLButtonElement} back
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} fields
 */

/**
 * Step-by-step Engraved & Personalized Gifts builder, modeled on the signs builder
 * (assets/signs-builder.js) but kept separate from it. Options, prices and variant ids are
 * read from sections/gifts-builder-product.liquid through the Section Rendering API, so a
 * combination with no variant is never offered and never sent to the cart.
 *
 * @extends {Component<GiftsBuilderRefs>}
 */
class GiftsBuilder extends Component {
  requiredRefs = [
    'form', 'chips', 'price', 'stage', 'progress', 'status', 'uploadBox', 'file', 'fileVector', 'fileRaster', 'fileType',
    'textBox', 'text', 'quantityLabel', 'quantity', 'packLine', 'total', 'notes', 'submit', 'back', 'next', 'fields',
  ];

  #current = 0;
  /** The chosen item tile's answers. @type {{ title: string, url: string, packUnit: string } | null} */
  #item = null;
  /** Every option of the chosen product, in Shopify's order. @type {ProductOption[]} */
  #options = [];
  /** The options with something to choose; each one is a step. @type {ProductOption[]} */
  #choosable = [];
  /** The value chosen on each option step, by position in #choosable. @type {string[]} */
  #picked = [];
  /** The variant for the item and its options. @type {{ id: string, price: string, cents: number } | null} */
  #variant = null;
  /** How the customer gives us the design: "upload", "text", "later", or '' before choosing. */
  #art = '';
  #vectorize = false;
  #quantity = 1;
  #busy = false;
  #sending = false;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #sendingTimer;
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {AbortController | undefined} */
  #request;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const { form, fallback } = this.refs;

    this.addEventListener('click', this.#onClick, { signal });
    form.addEventListener('submit', this.#onSubmit, { signal });
    form.addEventListener('change', this.#onChange, { signal });
    form.addEventListener('input', this.#onInput, { signal });
    // Coming back with the browser's back button: allow sending again.
    window.addEventListener('pageshow', () => this.#setSending(false), { signal });

    if (fallback) fallback.hidden = true;
    form.hidden = false;

    // ?item=<product handle> opens with that item already chosen.
    const wanted = new URLSearchParams(window.location.search).get('item');
    const tile = wanted ? this.#tiles('item').find((candidate) => candidate.dataset.handle === wanted) : undefined;
    if (tile) this.#choose(tile);
    else this.#render(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    clearTimeout(this.#sendingTimer);
  }

  /**
   * @param {string} key
   * @returns {HTMLElement | null}
   */
  #panel(key) {
    return this.querySelector(`[data-panel="${key}"]`);
  }

  /**
   * @param {string} key
   * @returns {HTMLElement[]} Every tile shown on a step.
   */
  #tiles(key) {
    return Array.from(this.querySelectorAll(`.hat-builder__tile[data-step="${key}"]`));
  }

  /** @returns {string[]} The steps that apply to the chosen item. Before one is chosen, no option steps. */
  #steps() {
    return ORDER.filter((key) => !key.startsWith('option') || Number(key.slice(6)) < this.#choosable.length);
  }

  /**
   * @param {HTMLElement} tile - An option value tile.
   * @param {boolean} last - True on the product's last option step, where the tile is one exact variant.
   * @returns {boolean} True when the tile can still lead to a variant that can be bought.
   */
  #purchasable(tile, last) {
    if (last) return Boolean(tile.dataset.variantId) && tile.dataset.variantAvailable === 'true';
    return Boolean(tile.dataset.lowestCents);
  }

  /**
   * The single-value "Pack" option of the chosen product, read as a count.
   * @returns {{ label: string, count: number } | null} For "Pack of 100": { label: "Pack of 100", count: 100 }.
   */
  #pack() {
    const option = this.#options.find((candidate) => candidate.name.trim().toLowerCase() === 'pack' && candidate.tiles.length === 1);
    const label = option?.tiles[0]?.dataset.value ?? '';
    if (!label) return null;
    return { label, count: Number(label.match(/\d+/)?.[0] ?? 0) };
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string} The chosen file's extension in lower case, or '' when no file is chosen.
   */
  #extension(input) {
    const file = input.files?.[0];
    return file ? (file.name.split('.').pop() ?? '').toLowerCase() || 'unknown' : '';
  }

  /** @returns {boolean} True when the uploaded file is a PNG or JPG. */
  #raster() {
    return this.#art === 'upload' && RASTER_TYPES.includes(this.#extension(this.refs.file));
  }

  /** @returns {boolean} True when the service is offered, the file is a PNG or JPG and the customer added it. */
  #service() {
    return Boolean(this.dataset.serviceVariantId) && this.#raster() && this.#vectorize;
  }

  /** @returns {string} What the artwork step's selection says, or '' while it is unanswered. */
  #artAnswer() {
    const { file, text } = this.refs;
    const data = this.dataset;
    if (this.#art === 'upload') {
      const type = this.#extension(file);
      return VECTOR_TYPES.includes(type) || RASTER_TYPES.includes(type) ? file.files?.[0]?.name ?? '' : '';
    }
    if (this.#art === 'text') return text.value.trim();
    if (this.#art === 'later') return data.artLater ?? '';
    return '';
  }

  /**
   * @param {string} key
   * @returns {boolean} True when the step has its answer.
   */
  #answered(key) {
    if (key === 'item') return Boolean(this.#item);
    if (key.startsWith('option')) return Boolean(this.#picked[Number(key.slice(6))]);
    if (key === 'art') return this.#artAnswer() !== '';
    return this.#quantity >= 1;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.refs.stage.setAttribute('aria-busy', String(busy));
    if (busy) this.#showStatus('loading');
  }

  /**
   * Blocks a second send while the first is uploading. If the browser never leaves the
   * page (for example the connection dropped), sending is allowed again after a while.
   * @param {boolean} sending
   */
  #setSending(sending) {
    clearTimeout(this.#sendingTimer);
    this.#sending = sending;
    this.#showStatus(sending ? 'sending' : '');
    if (sending) this.#sendingTimer = setTimeout(() => this.#setSending(false), 30000);
  }

  /** @param {'' | 'loading' | 'error' | 'unavailable' | 'sending'} kind */
  #showStatus(kind) {
    const { status } = this.refs;
    status.textContent = kind ? status.dataset[kind] ?? '' : '';
    status.hidden = !kind;
  }

  /** @returns {boolean} True only when every step is answered and the choices are a real variant. */
  #ready() {
    return this.#steps().every((key) => this.#answered(key)) && Boolean(this.#variant?.id) && !this.#busy;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.refs.moneyFormat?.content.textContent || '{{amount}}';
    return formatMoney(cents, format, this.dataset.currency ?? '');
  }

  /** Clears the artwork step, for a new item or a different way of giving the design. */
  #clearArt() {
    this.refs.file.value = '';
    this.refs.text.value = '';
    this.#vectorize = false;
  }

  /**
   * Ask Shopify for the product's option tiles, resolved for the given option values.
   * @param {string[]} ids
   * @returns {Promise<HTMLElement>}
   */
  async #fetchOptions(ids) {
    if (!this.#item?.url) throw new Error('error');

    this.#request?.abort();
    this.#request = new AbortController();
    this.#setBusy(true);

    const url = new URL(this.#item.url, window.location.origin);
    url.searchParams.set('section_id', this.dataset.section ?? '');
    const selected = ids.filter(Boolean);
    if (selected.length) url.searchParams.set('option_values', selected.join(','));

    const response = await fetch(url, { signal: this.#request.signal });
    if (!response.ok) throw new Error('error');
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const root = html.querySelector('.gifts-builder-product');
    if (!(root instanceof HTMLElement)) throw new Error('error');

    this.#setBusy(false);
    return root;
  }

  /**
   * Reads the options Shopify rendered and puts each choosable option's tiles in its panel.
   * @param {HTMLElement} root
   */
  #readOptions(root) {
    const earlier = this.#options;
    this.#options = Array.from(root.querySelectorAll('[data-option]')).map((group, index) => {
      const tiles = /** @type {HTMLElement[]} */ (Array.from(group.querySelectorAll('.hat-builder__tile')));
      const selected = tiles.find((tile) => tile.dataset.selected === 'true')?.dataset.optionValueId ?? '';
      return {
        name: /** @type {HTMLElement} */ (group).dataset.option ?? '',
        tiles,
        // A choice already made stays; otherwise Shopify's default keeps the request complete.
        id: earlier[index]?.id || selected,
      };
    });
    this.#choosable = this.#options.filter((option) => option.tiles.length > 1).slice(0, 3);

    this.#choosable.forEach((option, index) => {
      const key = `option${index}`;
      const last = index === this.#choosable.length - 1;
      const panel = this.#panel(key);
      const box = this.querySelector(`[data-tiles="${key}"]`);
      if (!panel || !box) return;
      panel.dataset.label = option.name;
      const heading = panel.querySelector('[data-option-heading]');
      if (heading) heading.textContent = option.name;

      box.replaceChildren(
        ...option.tiles.map((source) => {
          const tile = /** @type {HTMLElement} */ (document.importNode(source, true));
          tile.dataset.step = key;
          tile.hidden = !this.#purchasable(tile, last);
          this.#decorate(tile, option.name, last);
          return tile;
        })
      );
    });
  }

  /**
   * Writes a tile's price and shows its color swatch.
   * @param {HTMLElement} tile
   * @param {string} optionName
   * @param {boolean} last
   */
  #decorate(tile, optionName, last) {
    const data = tile.dataset;
    const note = tile.querySelector('[data-note]');
    if (note instanceof HTMLElement) {
      // The last step shows the exact price. Earlier steps say "from" when the later choices change it.
      const ranged = data.lowestCents !== data.highestCents;
      const price = last ? data.price ?? '' : data.lowestPrice ?? '';
      note.textContent = !last && ranged && price ? (this.dataset.fromLabel ?? '[price]').replace('[price]', price) : price;
      note.hidden = !price;
    }

    const dot = tile.querySelector('[data-swatch-dot]');
    if (!(dot instanceof HTMLElement) || optionName.trim().toLowerCase() !== 'color') return;
    const name = (data.value ?? '').trim().toLowerCase();
    const known = /** @type {Record<string, string>} */ (SWATCHES)[name];
    // In order: the swatch set in Shopify, a known name, or a name the browser knows as a color.
    const color = data.swatch || known || (window.CSS?.supports?.('color', name) ? name : '');
    if (!color) return;
    dot.style.backgroundColor = color;
    dot.hidden = false;
  }

  async #loadItem() {
    this.#options = [];
    const root = await this.#fetchOptions([]);
    this.#readOptions(root);

    if (this.#choosable.length) {
      if (!this.#tiles('option0').some((tile) => !tile.hidden)) throw new Error('unavailable');
      return;
    }
    // Nothing to choose: the product's own variant is the one to buy.
    const data = root.dataset;
    if (!data.variantId || data.variantAvailable !== 'true') throw new Error('unavailable');
    this.#variant = { id: data.variantId, price: data.price ?? '', cents: Number(data.priceCents) || 0 };
  }

  /** @param {HTMLElement} tile */
  async #choose(tile) {
    const key = tile.dataset.step ?? '';
    const value = tile.dataset.value ?? '';
    if (this.#busy || !key || !value) return;

    if (key === 'art') {
      // One way only: choosing another clears the file and the text.
      if (this.#art !== value) {
        this.#clearArt();
        this.#art = value;
      }
      this.#render(false);
      if (value === 'upload') this.refs.file.focus();
      if (value === 'text') this.refs.text.focus();
      return;
    }

    try {
      if (key === 'item' && this.#item?.title !== value) {
        this.#item = { title: value, url: tile.dataset.url ?? '', packUnit: tile.dataset.packUnit ?? '' };
        this.#picked = [];
        this.#choosable = [];
        this.#variant = null;
        this.#art = '';
        this.#clearArt();
        this.#quantity = 1;
        this.refs.quantity.value = '1';
        await this.#loadItem();
      } else if (key.startsWith('option')) {
        const index = Number(key.slice(6));
        const option = this.#choosable[index];
        const last = index === this.#choosable.length - 1;
        if (option && this.#picked[index] !== value) {
          // A different choice here clears the option choices after it. The artwork is kept.
          this.#picked = [...this.#picked.slice(0, index), value];
          this.#variant = null;
          option.id = tile.dataset.optionValueId ?? '';
          if (last) {
            this.#variant = { id: tile.dataset.variantId ?? '', price: tile.dataset.price ?? '', cents: Number(tile.dataset.priceCents) || 0 };
          } else {
            this.#readOptions(await this.#fetchOptions(this.#options.map((candidate) => candidate.id)));
            if (!this.#tiles(`option${index + 1}`).some((candidate) => !candidate.hidden)) throw new Error('unavailable');
          }
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (key === 'item') this.#item = null;
      else this.#picked = this.#picked.slice(0, Number(key.slice(6)));
      this.#setBusy(false);
      this.#showStatus(error instanceof Error && error.message === 'unavailable' ? 'unavailable' : 'error');
      this.#render(false);
      return;
    }

    this.#showStatus('');
    const steps = this.#steps();
    this.#current = Math.min(steps.indexOf(key) + 1, steps.length - 1);
    this.#render(true);
  }

  /** @param {boolean} moved - True when the step changed, to slide it in and move focus. */
  #render(moved) {
    const refs = this.refs;
    const steps = this.#steps();
    this.#current = Math.min(this.#current, steps.length - 1);
    const key = steps[this.#current] ?? 'item';

    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const active = panel.dataset.panel === key;
      panel.hidden = !active;
      panel.removeAttribute('data-enter');
      if (active && moved) {
        // Restart the slide-in animation.
        void panel.offsetWidth;
        panel.setAttribute('data-enter', '');
        const heading = panel.querySelector('.gifts-builder__panel-heading');
        if (heading instanceof HTMLElement) heading.focus();
      }
    }

    for (const tile of this.#tiles('item')) tile.setAttribute('aria-pressed', String(tile.dataset.value === this.#item?.title));
    this.#choosable.forEach((_, index) => {
      for (const tile of this.#tiles(`option${index}`)) tile.setAttribute('aria-pressed', String(tile.dataset.value === this.#picked[index]));
    });
    for (const tile of this.#tiles('art')) tile.setAttribute('aria-pressed', String(tile.dataset.value === this.#art));

    this.#renderArt();
    this.#renderQuantity();

    refs.progress.replaceChildren(
      ...steps.map((step, index) => {
        const segment = document.createElement('span');
        if (step === 'qty' ? false : this.#answered(step) && index <= this.#current) segment.setAttribute('data-done', '');
        return segment;
      })
    );

    // The exact variant price for the item and its options.
    refs.price.textContent = this.#variant?.price || '—';

    this.#updateTotals();
    refs.back.disabled = this.#current === 0;
    refs.next.disabled = !this.#answered(key) || this.#current >= steps.length - 1;
  }

  /** Shows the control for the chosen way of giving the design, and the messages under a file. */
  #renderArt() {
    const refs = this.refs;
    const type = this.#extension(refs.file);
    const raster = this.#raster();
    const offered = raster && Boolean(this.dataset.serviceVariantId);

    refs.uploadBox.hidden = this.#art !== 'upload';
    refs.textBox.hidden = this.#art !== 'text';
    if (refs.laterBox) refs.laterBox.hidden = this.#art !== 'later';

    refs.fileVector.hidden = !VECTOR_TYPES.includes(type);
    refs.fileRaster.hidden = !RASTER_TYPES.includes(type);
    refs.fileType.hidden = !type || VECTOR_TYPES.includes(type) || RASTER_TYPES.includes(type);
    if (refs.vectorize) {
      refs.vectorize.hidden = !offered;
      refs.vectorize.setAttribute('aria-pressed', String(this.#vectorize));
      for (const label of refs.vectorize.querySelectorAll('span')) label.hidden = label.hasAttribute('data-on') !== this.#vectorize;
    }
    if (refs.ownershipNotice) refs.ownershipNotice.hidden = !offered;
    if (refs.priceNote) refs.priceNote.textContent = (this.#service() ? this.dataset.priceNoteVectorization : this.dataset.priceNote) ?? '';

    // The lines above Add to cart that fit this way of giving the design.
    for (const notice of this.querySelectorAll('[data-notice-for]')) {
      if (notice instanceof HTMLElement) notice.hidden = !(notice.dataset.noticeFor ?? '').split(' ').includes(this.#art || 'upload');
    }
  }

  /** A product sold in packs counts packs, and says how many pieces that is. */
  #renderQuantity() {
    const { quantityLabel, packLine } = this.refs;
    const data = this.dataset;
    const pack = this.#pack();
    quantityLabel.textContent = (pack?.count ? (data.packsLabel ?? '').replace('[count]', String(pack.count)) : pack?.label || data.quantityLabel) ?? '';

    const line = pack?.count ? (this.#quantity === 1 ? data.packLineOne : data.packLineOther) ?? '' : '';
    packLine.textContent = line
      .replace('[packs]', String(this.#quantity))
      .replace('[total]', String(this.#quantity * (pack?.count ?? 0)))
      .replace('[unit]', this.#item?.packUnit || data.packUnit || '');
    packLine.hidden = !line;
  }

  /** The customer's choices so far. Tapping one goes back to that step. */
  #renderChips() {
    const steps = this.#steps();
    const data = this.dataset;
    const chips = [];
    for (const key of steps) {
      let text = '';
      if (key === 'item') text = this.#item?.title ?? '';
      else if (key.startsWith('option')) text = this.#picked[Number(key.slice(6))] ?? '';
      else if (key === 'art') {
        const answer = this.#artAnswer();
        text = answer && this.#art === 'text' ? `${data.artText}: ${answer}` : answer;
      }
      // The quantity shows once the customer has reached that step.
      else if (this.#variant && this.#current === steps.length - 1) text = String(this.#quantity);
      if (!text) continue;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'gifts-builder__chip';
      chip.dataset.chip = key;

      const label = document.createElement('span');
      label.className = 'gifts-builder__chip-label';
      label.textContent = (key === 'qty' ? this.refs.quantityLabel.textContent : this.#panel(key)?.dataset.label) ?? '';
      const value = document.createElement('span');
      value.className = 'gifts-builder__chip-value';
      value.textContent = text.length > 60 ? `${text.slice(0, 57).trimEnd()}…` : text;
      chip.append(label, value);
      chips.push(chip);
    }
    this.refs.chips.replaceChildren(...chips);
    if (this.refs.empty) this.refs.empty.hidden = chips.length > 0;
  }

  /** Refreshes the total, the selections and the Add to cart button. */
  #updateTotals() {
    const { total, submit } = this.refs;
    const priced = Boolean(this.#variant?.id);
    const sum = priced ? this.#money((this.#variant?.cents ?? 0) * this.#quantity) : '';
    total.textContent = sum || '—';
    submit.textContent = (this.dataset.addLabel ?? '').replace('[price]', sum).replace(/[\s-]+$/, priced ? '$&' : '');
    submit.disabled = !this.#ready();
    this.#renderChips();
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const { back, next, vectorize } = this.refs;

    const tile = target.closest('.hat-builder__tile');
    if (tile instanceof HTMLElement && this.contains(tile)) {
      this.#choose(tile);
      return;
    }

    const chip = target.closest('.gifts-builder__chip');
    if (chip instanceof HTMLElement && !this.#busy) {
      this.#current = Math.max(0, this.#steps().indexOf(chip.dataset.chip ?? ''));
      this.#render(true);
      return;
    }

    if (back.contains(target) || next.contains(target)) {
      if (this.#busy) return;
      this.#current = Math.min(this.#steps().length - 1, Math.max(0, this.#current + (back.contains(target) ? -1 : 1)));
      this.#render(true);
      return;
    }

    if (vectorize?.contains(target)) {
      this.#vectorize = !this.#vectorize;
      this.#render(false);
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const { quantity, file } = this.refs;
    // Leaving the number field: show the number that will be used (never below 1).
    if (event.target === quantity) quantity.value = String(this.#quantity);
    if (event.target === file) {
      // A new file starts without the service.
      this.#vectorize = false;
      this.#render(false);
    }
  };

  /**
   * Typing a quantity or the customer's text updates the page straight away.
   * @param {Event} event
   */
  #onInput = (event) => {
    const { quantity, text } = this.refs;
    if (event.target === quantity) {
      this.#quantity = Math.max(1, Math.floor(Number(quantity.value)) || 1);
      this.#renderQuantity();
      this.#updateTotals();
    } else if (event.target === text) {
      this.refs.next.disabled = !this.#answered('art');
      this.#updateTotals();
    }
  };

  /**
   * Writes the cart fields, then lets the browser post the form (multipart, so the file is
   * uploaded as a line item property). Shopify answers by showing the cart.
   * @param {SubmitEvent} event
   */
  #onSubmit = (event) => {
    if (!this.#ready() || this.#sending || !this.#variant) {
      event.preventDefault();
      return;
    }

    const { file, text, notes, fields } = this.refs;
    const service = this.#service();
    // The gift alone uses Shopify's plain product fields. With the service it becomes a list of "items".
    const field = (/** @type {string} */ name) => (service ? `items[0][${name}]` : name);
    const property = (/** @type {string} */ name) => (service ? `items[0][properties][${name}]` : `properties[${name}]`);

    /** @type {[string, string][]} */
    const values = [
      [field('id'), this.#variant.id],
      [field('quantity'), String(this.#quantity)],
    ];
    if (this.#art === 'text') values.push([property(text.dataset.property ?? 'Text'), text.value.trim()]);
    if (this.#art === 'later') values.push([property(file.dataset.property ?? 'Artwork'), 'Design later']);
    const comment = notes.value.trim();
    if (comment) values.push([property(notes.dataset.property ?? 'Notes'), comment]);
    if (service) {
      values.push(
        ['items[1][id]', this.dataset.serviceVariantId ?? ''],
        ['items[1][quantity]', '1'],
        ['items[1][properties][For]', `${this.#item?.title ?? ''} - ${file.dataset.property ?? 'Artwork'}`]
      );
    }

    // Only an uploaded file gets a name, so nothing else is uploaded.
    file.name = this.#art === 'upload' ? property(file.dataset.property ?? 'Artwork') : '';
    fields.replaceChildren(
      ...values.map(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        return input;
      })
    );
    this.#setSending(true);
  };
}

if (!customElements.get('gifts-builder')) {
  customElements.define('gifts-builder', GiftsBuilder);
}
